import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const location = useLocation();
  
  // Hide footer on landing pages
  if (location.pathname === '/lp') {
    return null;
  }

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Main Footer Content */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
            
            {/* Company Info - Larger Column */}
            <div className="lg:col-span-4 space-y-6">
              <div>
                <img 
                  src="/logo-white.png" 
                  alt="Easy Trademark Filing" 
                  className="h-14 w-auto mb-4"
                />
                <p className="text-gray-400 text-sm leading-relaxed">
                  Making trademark registration simple and affordable for businesses of all sizes. We handle the legal complexity so you can focus on building your brand.
                </p>
              </div>



              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 pt-4">
                <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg text-xs border border-white/20">
                  🛡️ USPTO Registered
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg text-xs border border-white/20">
                  ⭐ A+ Rated
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg text-xs border border-white/20">
                  ✓ 5000+ Clients
                </div>
              </div>
            </div>

            {/* Services Column */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-bold mb-4 text-white">Services</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/services/trademark-registration" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-red-500 group-hover:translate-x-1 transition-transform" />
                    Trademark Registration
                  </Link>
                </li>
                <li>
                  <Link to="/services/copyright-registration" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-red-500 group-hover:translate-x-1 transition-transform" />
                    Copyright Services
                  </Link>
                </li>
                <li>
                  <Link to="/services/patent-search" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-red-500 group-hover:translate-x-1 transition-transform" />
                    Patent Services
                  </Link>
                </li>
                <li>
                  <Link to="/services/llc" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-red-500 group-hover:translate-x-1 transition-transform" />
                    LLC Formation
                  </Link>
                </li>
                <li>
                  <Link to="/services/c-corporation" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-red-500 group-hover:translate-x-1 transition-transform" />
                    C Corporation
                  </Link>
                </li>
                <li>
                  <Link to="/services/s-corporation" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-red-500 group-hover:translate-x-1 transition-transform" />
                    S Corporation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-bold mb-4 text-white">Company</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Media Center Column */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-bold mb-4 text-white">Media Center</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/media/blogs" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    Blogs
                  </Link>
                </li>
                <li>
                  <Link to="/media/news-updates" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    News & Updates
                  </Link>
                </li>
                <li>
                  <Link to="/media/publications" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="w-3 h-3 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    Publications
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-bold mb-4 text-white">Contact</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Address</p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    515 Flower St, 17th Floors<br />
                    Los Angeles, CA 90071<br />
                    United States
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">UAN</p>
                  <a href="tel:+18887130129" className="text-sm text-gray-300 hover:text-white transition-colors">
                    (888) 713-0129
                  </a>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Email</p>
                  <a href="mailto:info@easytrademarkfiling.com" className="text-sm text-gray-300 hover:text-white transition-colors">
                    info@easytrademarkfiling.com
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">
              © 2026 Easy Trademark Filing. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-600">•</span>
              <Link to="/terms-of-service" className="hover:text-white transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;